import React, { useEffect, useMemo, useState } from 'react';
import { CheckCircle2, Loader2, XCircle } from 'lucide-react';
import { buildApiUrl } from '../../utils/apiUrl';

function fmtN(value?: number | string | null) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '-';
  return n.toLocaleString('es-CR', { minimumFractionDigits: 3, maximumFractionDigits: 3 });
}

interface PlanProducto {
  id: number;
  articulo: string;
  nombre_producto: string;
  descripcion?: string | null;
  dosis: number;
  volumen?: number | null;
  medida: string;
  grupo_quimico?: string | null;
  reingreso?: string | null;
}

export function AjusteSolicitudPage({ token }: { token: string }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState<any>(null);
  const [error, setError] = useState('');
  const [cantidad, setCantidad] = useState('');
  const [comentario, setComentario] = useState('');
  // Para cambio de producto
  const [planProductos, setPlanProductos] = useState<PlanProducto[]>([]);
  const [busquedaProd, setBusquedaProd] = useState('');
  const [productoElegido, setProductoElegido] = useState<PlanProducto | null>(null);
  const [dosisManual, setDosisManual] = useState<string>('');

  const solicitud = data?.solicitud;
  const boleta = data?.boleta;
  const resuelta = solicitud && solicitud.estado !== 'pendiente';
  const vencida = data?.expired;
  const esReversion = solicitud?.tipo === 'reversion';
  const esCambioProducto = solicitud?.tipo === 'producto';
  const esNuevoProducto = solicitud?.tipo === 'nuevo';
  const esExcluir = solicitud?.tipo === 'excluir';
  const requiereSeleccion = esCambioProducto || esNuevoProducto;

  useEffect(() => {
    let active = true;
    setLoading(true);
    fetch(buildApiUrl(`/aplicaciones/ajuste-solicitudes/${token}`))
      .then(async (resp) => {
        const payload = await resp.json().catch(() => ({}));
        if (!resp.ok) throw new Error(payload?.error || 'No se pudo cargar la solicitud');
        return payload;
      })
      .then((payload) => {
        if (!active) return;
        setData(payload);
        const cantidadIni = Number(payload?.solicitud?.cantidad_solicitada);
        setCantidad(Number.isFinite(cantidadIni)
          ? cantidadIni.toLocaleString('es-CR', { minimumFractionDigits: 3, maximumFractionDigits: 3 })
          : ''
        );
        // Si requiere selección (cambio o agregar), cargar el catálogo de productos del plan
        if (payload?.solicitud?.tipo === 'producto' || payload?.solicitud?.tipo === 'nuevo') {
          fetch(buildApiUrl(`/aplicaciones/ajuste-solicitudes/${token}/plan-productos`))
            .then(async (r) => {
              const p = await r.json().catch(() => ({}));
              if (active && r.ok) setPlanProductos(p.productos || []);
            })
            .catch(() => { /* ignore */ });
        }
      })
      .catch((err) => active && setError(err?.message || 'No se pudo cargar la solicitud'))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [token]);

  const estadoTexto = useMemo(() => {
    if (!solicitud) return '';
    if (vencida && solicitud.estado === 'pendiente') return 'Vencida';
    if (solicitud.estado === 'aprobada') return 'Aprobada';
    if (solicitud.estado === 'modificada') return 'Modificada';
    if (solicitud.estado === 'rechazada') return 'Rechazada';
    return 'Pendiente';
  }, [solicitud, vencida]);

  async function resolver(decision: 'aprobada' | 'rechazada' | 'modificada') {
    setSaving(true);
    setError('');
    try {
      // Acepta coma o punto como separador decimal (formato CR vs ANSI)
      const parseCantidad = (s: string) => {
        const n = parseFloat(String(s).replace(/,(?=\d{3}\b)/g, '').replace(',', '.'));
        return Number.isFinite(n) ? n : NaN;
      };
      const cantidadEditada = parseCantidad(cantidad);
      if (decision === 'modificada' && !requiereSeleccion && !Number.isFinite(cantidadEditada)) {
        setError('Cantidad inválida');
        setSaving(false);
        return;
      }
      // Si requiere selección (cambio o agregar producto), validar elección
      if (requiereSeleccion && (decision === 'aprobada' || decision === 'modificada') && !productoElegido) {
        setError(esNuevoProducto ? 'Elegí el producto a agregar antes de aprobar' : 'Elegí el producto sustituto antes de aprobar');
        setSaving(false);
        return;
      }
      const parseDosis = (s: string) => {
        const n = parseFloat(String(s).replace(/,(?=\d{3}\b)/g, '').replace(',', '.'));
        return Number.isFinite(n) ? n : NaN;
      };
      const dosisFinal = requiereSeleccion ? parseDosis(dosisManual) : NaN;
      if (requiereSeleccion && (decision === 'aprobada' || decision === 'modificada')
          && (!Number.isFinite(dosisFinal) || dosisFinal <= 0)) {
        setError('Ingresá una dosis válida');
        setSaving(false);
        return;
      }
      const resp = await fetch(buildApiUrl(`/aplicaciones/ajuste-solicitudes/${token}/resolver`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          decision,
          cantidad_aprobada: requiereSeleccion
            ? undefined
            : decision === 'modificada' ? cantidadEditada : Number(solicitud?.cantidad_solicitada),
          comentario,
          producto_aprobado_id: requiereSeleccion && productoElegido ? productoElegido.id : undefined,
          dosis_aprobada: requiereSeleccion ? dosisFinal : undefined,
        }),
      });
      const payload = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(payload?.error || 'No se pudo resolver');
      setData((prev: any) => ({ ...prev, solicitud: payload.solicitud, expired: false }));
    } catch (err: any) {
      setError(err?.message || 'No se pudo resolver');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="min-h-screen px-4 py-5" style={{ background: '#07111f', color: '#f8fafc' }}>
      <div className="mx-auto max-w-md">
        <div className="mb-4">
          <div className="text-xs font-semibold uppercase tracking-wide" style={{ color: '#93c5fd' }}>Control de Piña</div>
          <h1 className="mt-1 text-xl font-bold">
            {esReversion ? 'Solicitud de reversión' : esCambioProducto ? 'Cambio de producto' : esNuevoProducto ? 'Agregar producto' : esExcluir ? 'Excluir producto' : 'Aprobación de ajuste'}
          </h1>
        </div>

        {loading && (
          <div className="flex items-center gap-2 rounded-lg border p-4" style={{ borderColor: '#1f2f46', background: '#0f1d31' }}>
            <Loader2 size={18} className="animate-spin" /> Cargando solicitud...
          </div>
        )}

        {!loading && error && (
          <div className="rounded-lg border p-4 text-sm" style={{ borderColor: '#7f1d1d', background: '#35131a', color: '#fecaca' }}>{error}</div>
        )}

        {!loading && solicitud && (
          <div className="rounded-lg border p-4" style={{ borderColor: '#1f2f46', background: '#0f1d31' }}>
            <div className="mb-3 flex items-start justify-between gap-3">
              <div>
                <div className="text-xs" style={{ color: '#93a4bb' }}>Boleta</div>
                <div className="text-2xl font-extrabold" style={{ fontFamily: 'ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif', letterSpacing: '0.02em' }}>
                  #{boleta?.numero || solicitud.boleta_id}
                </div>
              </div>
              <span className="rounded-full px-3 py-1 text-xs font-bold" style={{ background: resuelta ? '#123a2a' : '#3b2800', color: resuelta ? '#86efac' : '#fbbf24' }}>
                {estadoTexto}
              </span>
            </div>

            {/* Producto original solo si la solicitud refiere a uno existente */}
            {!esReversion && !esNuevoProducto && (
              <div className="mb-4">
                <div className="text-xs" style={{ color: '#93a4bb' }}>Producto</div>
                <div className="text-base font-bold">{solicitud.nombre_producto || solicitud.articulo}</div>
                <div className="text-sm" style={{ color: '#93a4bb' }}>{solicitud.articulo} · {solicitud.medida || ''}</div>
              </div>
            )}

            {!esReversion && (
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-md border p-3" style={{ borderColor: '#1f2f46', background: '#081222' }}>
                  <div className="text-xs" style={{ color: '#93a4bb' }}>{requiereSeleccion ? 'Área' : 'Planificado'}</div>
                  <div className="mt-1 text-2xl font-black" style={{ color: '#93c5fd' }}>
                    {requiereSeleccion ? `${fmtN(boleta?.area_ha)} ha` : fmtN(solicitud.cantidad_planificada)}
                  </div>
                </div>
                {!requiereSeleccion && (
                <div className="rounded-md border p-3" style={{ borderColor: '#854d0e', background: '#211704' }}>
                  <div className="text-xs" style={{ color: '#facc15' }}>Solicitado</div>
                  <div className="mt-1 text-2xl font-black" style={{ color: '#fbbf24' }}>{fmtN(solicitud.cantidad_solicitada)}</div>
                </div>
                )}
                {requiereSeleccion && (
                <div className="rounded-md border p-3" style={{ borderColor: '#1f2f46', background: '#081222' }}>
                  <div className="text-xs" style={{ color: '#93a4bb' }}>Boom</div>
                  <div className="mt-1 text-2xl font-black" style={{ color: '#93c5fd' }}>{fmtN(boleta?.boom_capacidad_l)} L</div>
                </div>
                )}
              </div>
            )}

            {/* Aviso especial para reversión */}
            {esReversion && (
              <div className="mb-4 rounded-md border p-3 text-sm" style={{ borderColor: '#854d0e', background: '#211704', color: '#fde68a' }}>
                <div className="font-bold mb-1" style={{ color: '#fbbf24' }}>⚠ Solicitud de reversión a PENDIENTE</div>
                Si aprobás, se borrará todo el trabajo del boomero (reporte de aplicación, recorrido GPS, booms aplicados) y la boleta volverá al estado pendiente para que bodega corrija el despacho.
              </div>
            )}

            {/* Aviso especial para exclusión */}
            {esExcluir && (
              <div className="mt-4 mb-2 rounded-md border p-3 text-sm" style={{ borderColor: '#7f1d1d', background: '#2a0c0c', color: '#fecaca' }}>
                <div className="font-bold mb-1" style={{ color: '#fca5a5' }}>⛔ Excluir producto</div>
                Si aprobás, este producto NO se despacha ni se aplica en la boleta. La fila queda en la planilla marcada como EXCLUIDA (para trazabilidad).
              </div>
            )}

            <div className="mt-4 rounded-md border p-3 text-sm" style={{ borderColor: '#1f2f46', background: '#081222', color: '#cbd5e1' }}>
              <div className="font-bold" style={{ color: '#f8fafc' }}>
                {esReversion ? 'Motivo de la reversión'
                  : esCambioProducto ? 'Motivo del cambio'
                  : esNuevoProducto ? 'Motivo del agregado'
                  : esExcluir ? 'Motivo de exclusión'
                  : solicitud.razon_codigo}
              </div>
              {solicitud.razon_detalle || 'Sin detalle adicional.'}
              {requiereSeleccion && solicitud.producto_sugerido_articulo && (
                <div className="mt-2 text-xs" style={{ color: '#a78bfa' }}>
                  Bodega sugiere: <span className="font-bold">{solicitud.producto_sugerido_articulo}</span>
                </div>
              )}
            </div>

            {/* Selector de producto (cambio o agregar) */}
            {requiereSeleccion && !resuelta && !vencida && (
              <div className="mt-4 rounded-md border p-3" style={{ borderColor: '#4c1d95', background: '#1a1033' }}>
                <div className="mb-2 flex items-center justify-between">
                  <div className="text-xs font-bold uppercase tracking-wide" style={{ color: '#c4b5fd' }}>
                    {productoElegido
                      ? (esNuevoProducto ? 'Producto a agregar elegido' : 'Producto sustituto elegido')
                      : (esNuevoProducto ? 'Elegir producto a agregar' : 'Elegir producto sustituto')}
                  </div>
                  {productoElegido && (
                    <button
                      onClick={() => { setProductoElegido(null); setDosisManual(''); setBusquedaProd(''); }}
                      className="text-xs underline"
                      style={{ color: '#a78bfa' }}
                    >
                      cambiar
                    </button>
                  )}
                </div>

                {productoElegido ? (
                  <div className="rounded-md border px-3 py-2 text-xs"
                       style={{ borderColor: '#a78bfa', background: '#2e1d5c', color: '#f8fafc' }}>
                    <div className="font-bold text-sm">{productoElegido.nombre_producto}</div>
                    <div style={{ color: '#c4b5fd' }}>
                      {productoElegido.articulo} · {productoElegido.medida}
                      {productoElegido.grupo_quimico ? ` · ${productoElegido.grupo_quimico}` : ''}
                    </div>
                  </div>
                ) : (
                  <>
                    <input
                      value={busquedaProd}
                      onChange={(e) => setBusquedaProd(e.target.value)}
                      placeholder="Buscar por nombre o artículo..."
                      className="mb-2 w-full rounded-md border px-3 py-2 text-sm outline-none"
                      style={{ borderColor: '#4c1d95', background: '#0f0820', color: '#f8fafc' }}
                    />
                    <div className="max-h-64 overflow-y-auto space-y-1">
                      {planProductos
                        .filter(p => {
                          if (!busquedaProd.trim()) return true;
                          const q = busquedaProd.toLowerCase();
                          return (p.nombre_producto || '').toLowerCase().includes(q)
                            || (p.articulo || '').toLowerCase().includes(q)
                            || (p.descripcion || '').toLowerCase().includes(q);
                        })
                        .slice(0, 50)
                        .map(p => (
                          <button
                            key={p.id}
                            onClick={() => {
                              setProductoElegido(p);
                              setDosisManual(Number(p.dosis || 0).toLocaleString('es-CR', { minimumFractionDigits: 3, maximumFractionDigits: 3 }));
                            }}
                            className="w-full text-left rounded-md border px-2 py-2 text-xs"
                            style={{
                              borderColor: '#2a1f4a',
                              background: '#13091f',
                              color: '#f8fafc',
                            }}
                          >
                            <div className="font-bold">{p.nombre_producto}</div>
                            <div style={{ color: '#93a4bb' }}>
                              {p.articulo} · {p.medida}
                              {p.grupo_quimico ? ` · ${p.grupo_quimico}` : ''}
                            </div>
                          </button>
                        ))}
                      {planProductos.length === 0 && (
                        <div className="text-xs text-center py-3" style={{ color: '#93a4bb' }}>
                          Cargando catálogo del plan...
                        </div>
                      )}
                    </div>
                  </>
                )}
                {productoElegido && (() => {
                  const area = Number(boleta?.area_ha || 0);
                  const boomCap = Number(boleta?.boom_capacidad_l || 0);
                  const nBooms = Number(boleta?.n_booms_completos || 0);
                  const parseN = (s: string) => {
                    const n = parseFloat(String(s).replace(/,(?=\d{3}\b)/g, '').replace(',', '.'));
                    return Number.isFinite(n) ? n : 0;
                  };
                  const dosis = parseN(dosisManual);
                  const vol = Number(productoElegido.volumen || 0);
                  // total = dosis × área
                  // por boom = dosis × (boom_capacidad / volumen)  → cuánto producto va en un boom completo
                  // fracción = total − (por boom × n_booms_completos)  → remanente del último boom
                  const cantidadAplicar = dosis * area;
                  const cantidadBoom = vol > 0 ? (dosis * boomCap) / vol : 0;
                  const cantidadFraccion = nBooms > 0
                    ? cantidadAplicar - (cantidadBoom * nBooms)
                    : cantidadAplicar;
                  return (
                    <div className="mt-3 space-y-2">
                      <div>
                        <div className="mb-1 text-xs font-bold" style={{ color: '#c4b5fd' }}>
                          Dosis a aplicar ({productoElegido.medida}/ha)
                        </div>
                        <input
                          value={dosisManual}
                          onChange={(e) => setDosisManual(e.target.value.replace(/[^0-9.,]/g, ''))}
                          inputMode="decimal"
                          placeholder="0,000"
                          className="w-full rounded-md border px-3 py-2 text-right text-lg font-bold outline-none"
                          style={{ borderColor: '#a78bfa', background: '#0f0820', color: '#f8fafc' }}
                        />
                      </div>
                      <div className="rounded-md border p-2 text-xs" style={{ borderColor: '#a78bfa', background: '#0f0820' }}>
                        <div className="font-bold mb-1" style={{ color: '#c4b5fd' }}>
                          Se aplicará ({productoElegido.medida})
                        </div>
                        <div className="grid grid-cols-3 gap-2" style={{ color: '#f8fafc' }}>
                          <div>
                            <div style={{ color: '#93a4bb' }}>Total</div>
                            <div className="font-bold">{fmtN(cantidadAplicar)}</div>
                          </div>
                          <div>
                            <div style={{ color: '#93a4bb' }}>Por boom</div>
                            <div className="font-bold">{fmtN(cantidadBoom)}</div>
                          </div>
                          <div>
                            <div style={{ color: '#93a4bb' }}>Fracción</div>
                            <div className="font-bold">{fmtN(cantidadFraccion)}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}

            {!resuelta && !vencida && (
              <div className="mt-4 space-y-3">
                {!esReversion && !requiereSeleccion && !esExcluir && (
                  <input
                    value={cantidad}
                    onChange={(e) => setCantidad(e.target.value.replace(/[^0-9.,]/g, ''))}
                    inputMode="decimal"
                    className="w-full rounded-md border px-3 py-3 text-right text-xl font-bold outline-none"
                    style={{ borderColor: '#1f2f46', background: '#07111f', color: '#f8fafc' }}
                  />
                )}
                <textarea
                  value={comentario}
                  onChange={(e) => setComentario(e.target.value)}
                  placeholder="Comentario opcional"
                  className="min-h-20 w-full rounded-md border px-3 py-2 text-sm outline-none"
                  style={{ borderColor: '#1f2f46', background: '#07111f', color: '#f8fafc' }}
                />
                <div className="grid grid-cols-2 gap-2">
                  <button
                    disabled={saving || (requiereSeleccion && !productoElegido)}
                    onClick={() => resolver('aprobada')}
                    className="flex items-center justify-center gap-2 rounded-md px-3 py-3 text-sm font-bold disabled:opacity-50"
                    style={{ background: '#14532d', color: '#bbf7d0' }}
                  >
                    <CheckCircle2 size={16} /> {esReversion ? 'Aprobar reversión' : esCambioProducto ? 'Aprobar cambio' : esNuevoProducto ? 'Aprobar y agregar' : esExcluir ? 'Aprobar exclusión' : 'Aceptar'}
                  </button>
                  <button disabled={saving} onClick={() => resolver('rechazada')} className="flex items-center justify-center gap-2 rounded-md px-3 py-3 text-sm font-bold" style={{ background: '#3f1218', color: '#fecaca' }}>
                    <XCircle size={16} /> Rechazar
                  </button>
                </div>
                {!esReversion && !requiereSeleccion && !esExcluir && (
                  <button disabled={saving} onClick={() => resolver('modificada')} className="w-full rounded-md px-3 py-3 text-sm font-bold" style={{ background: '#1e3a8a', color: '#dbeafe' }}>
                    Aprobar cantidad editada
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
